import { useState, FormEvent, ChangeEvent } from 'react'
import { Send, CheckCircle, AlertCircle } from 'lucide-react'
import emailjs from '@emailjs/browser'
import { EMAILJS_CONFIG } from '../config/emailjs'

interface FormData {
  name: string
  email: string
  message: string
}

type FormStatus = 'idle' | 'submitting' | 'success' | 'error'

export default function Contact() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    message: '',
  })
  const [status, setStatus] = useState<FormStatus>('idle')
  const [errors, setErrors] = useState<Partial<FormData>>({})

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required'
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email'
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name as keyof FormData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }))
    }
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    if (!validateForm()) return

    setStatus('submitting')

    try {
      await emailjs.send(
        EMAILJS_CONFIG.serviceId,
        EMAILJS_CONFIG.templateId,
        {
          from_name: formData.name,
          from_email: formData.email,
          message: formData.message,
          service_type: 'General Inquiry',
        },
        EMAILJS_CONFIG.publicKey
      )
      setStatus('success')
      setFormData({ name: '', email: '', message: '' })
    } catch {
      setStatus('error')
    }
  }

  return (
    <section
      id="contact"
      className="section-padding"
      style={{ backgroundColor: 'var(--color-cream)' }}
    >
      <div className="container-custom">
        <div className="grid md:grid-cols-2 gap-12 md:gap-16">
          {/* Content */}
          <div>
            <p
              className="text-sm font-medium tracking-[0.2em] uppercase mb-4"
              style={{ color: 'var(--color-gold)' }}
            >
              Get in Touch
            </p>

            <h2
              className="font-heading text-4xl md:text-5xl mb-6"
              style={{ color: 'var(--color-dark)' }}
            >
              Let's Connect
            </h2>

            <p
              className="text-base md:text-lg leading-relaxed mb-8"
              style={{ color: 'var(--color-dark-light)' }}
            >
              Have questions about classes or want to learn more about working
              together? I'd love to hear from you. Send me a message and I'll
              get back to you soon.
            </p>

            <div className="space-y-4">
              <p
                className="text-sm"
                style={{ color: 'var(--color-dark-light)' }}
              >
                <span className="font-medium" style={{ color: 'var(--color-dark)' }}>
                  Instagram:
                </span>{' '}
                <a
                  href="https://www.instagram.com/omwithmadi/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline underline-offset-4"
                >
                  @omwithmadi
                </a>
              </p>
            </div>
          </div>

          {/* Form */}
          <div>
            {status === 'success' ? (
              <div
                className="h-full flex flex-col items-center justify-center text-center p-8"
                style={{ backgroundColor: 'var(--color-cream-light)' }}
              >
                <CheckCircle
                  size={48}
                  className="mb-4"
                  style={{ color: 'var(--color-olive)' }}
                />
                <h3
                  className="font-heading text-2xl mb-2"
                  style={{ color: 'var(--color-dark)' }}
                >
                  Message Sent
                </h3>
                <p style={{ color: 'var(--color-dark-light)' }}>
                  Thank you for reaching out. I'll be in touch soon!
                </p>
                <button
                  onClick={() => setStatus('idle')}
                  className="mt-6 text-sm font-medium underline underline-offset-4"
                  style={{ color: 'var(--color-gold)' }}
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name */}
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium mb-2"
                    style={{ color: 'var(--color-dark)' }}
                  >
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border focus:outline-none focus:ring-2 transition-shadow"
                    style={{
                      borderColor: errors.name ? '#dc2626' : 'var(--color-sage)',
                      color: 'var(--color-dark)',
                    }}
                    placeholder="Your name"
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium mb-2"
                    style={{ color: 'var(--color-dark)' }}
                  >
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border focus:outline-none focus:ring-2 transition-shadow"
                    style={{
                      borderColor: errors.email ? '#dc2626' : 'var(--color-sage)',
                      color: 'var(--color-dark)',
                    }}
                    placeholder="your@email.com"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>

                {/* Message */}
                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-medium mb-2"
                    style={{ color: 'var(--color-dark)' }}
                  >
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={5}
                    className="w-full px-4 py-3 bg-white border focus:outline-none focus:ring-2 transition-shadow resize-none"
                    style={{
                      borderColor: errors.message ? '#dc2626' : 'var(--color-sage)',
                      color: 'var(--color-dark)',
                    }}
                    placeholder="Tell me about your yoga goals..."
                  />
                  {errors.message && (
                    <p className="mt-1 text-sm text-red-600">{errors.message}</p>
                  )}
                </div>

                {/* Error message */}
                {status === 'error' && (
                  <div className="flex items-center gap-2 text-red-600">
                    <AlertCircle size={16} />
                    <p className="text-sm">Something went wrong. Please try again.</p>
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {status === 'submitting' ? (
                    'Sending...'
                  ) : (
                    <>
                      Send Message
                      <Send size={16} />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
